/**
 * PII sanitizer for analytics events.
 * Strips emails, phones, SSN/ITIN before pushing to dataLayer.
 * Never sends raw PII to GTM / GA4 / Meta / TikTok / Clarity.
 */

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
// Phone: 7+ digits possibly with separators (covers US and intl loosely)
const PHONE_RE = /(?:\+?\d[\s().-]?){7,}\d/g;
// SSN / ITIN: 9 digits with optional dashes (XXX-XX-XXXX). ITIN starts with 9 but same shape.
const SSN_RE = /\b\d{3}-?\d{2}-?\d{4}\b/g;

const REDACTED = "[redacted]";

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function scrubString(s: string): string {
  return s
    .replace(EMAIL_RE, REDACTED)
    .replace(SSN_RE, REDACTED)
    .replace(PHONE_RE, REDACTED);
}

// Keys we never want to forward, even if non-PII looking.
const FORBIDDEN_KEYS = new Set([
  "email",
  "phone",
  "ssn",
  "itin",
  "tax_id",
  "password",
  "token",
  "intake_token",
  "access_token",
  "refresh_token",
  "session_id",
]);

export function sanitizeParams<T>(input: T): T {
  if (input == null) return input;
  if (typeof input === "string") return scrubString(input) as unknown as T;
  if (Array.isArray(input)) return input.map((v) => sanitizeParams(v)) as unknown as T;
  if (isPlainObject(input)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (FORBIDDEN_KEYS.has(k.toLowerCase())) {
        out[k] = REDACTED;
        continue;
      }
      out[k] = sanitizeParams(v);
    }
    if (import.meta.env.DEV) {
      const raw = JSON.stringify(input);
      const clean = JSON.stringify(out);
      if (raw !== clean) {
        // eslint-disable-next-line no-console
        console.warn("[analytics] PII redacted from event payload");
      }
    }
    return out as unknown as T;
  }
  return input;
}
